import React from "react";
import {
  Eye,
  Trash2,
  Smartphone,
  KeyRound,
  RefreshCw,
  UserMinus,
} from "lucide-react";
import { Vehicle } from "../../types/vehicles";
import type { TruckDevice } from "../../types/devices";
import { useTranslation } from "react-i18next";
import ActionMenu, { ActionMenuItem } from "../UI/ActionMenu";

interface VehicleTableProps {
  vehicles: Vehicle[];
  selectedIds: string[];
  onSelectAll: (checked: boolean) => void;
  onSelectOne: (id: string, checked: boolean) => void;
  onEdit: (vehicle: Vehicle) => void;
  onDelete: (id: string) => void;
  loading: boolean;

  /**
   * ── The truck's screen, shown on the truck's own row ────────────────────────
   *
   * A device belongs to a vehicle — one screen per truck, showing that truck's
   * containers — so this is where it belongs, rather than on a page of its own
   * that made an admin hold two lists in their head to answer "does ABC-123 have
   * a screen, and who is on it".
   *
   * Every part is optional and the column appears only when `devices` is
   * supplied, so a caller that knows nothing about devices renders exactly the
   * table it always did.
   *
   * Trailers are skipped throughout: there is no cab to put a screen in, and
   * containers are dispatched to trucks.
   */
  devices?: Record<string, TruckDevice>;
  /** Register a device against this truck and issue its first code. */
  onAddDevice?: (vehicle: Vehicle) => void;
  /** Read back the code already outstanding — before reaching for a new one. */
  onShowCode?: (device: TruckDevice) => void;
  /** A fresh code. Takes an in-service screen out of use until redeemed. */
  onNewCode?: (device: TruckDevice) => void;
  /** End a shift somebody left open on the screen. */
  onSignOut?: (device: TruckDevice) => void;
  /** Take the screen out of service entirely. */
  onRevokeDevice?: (device: TruckDevice) => void;
}

export const VehicleTable: React.FC<VehicleTableProps> = ({
  vehicles,
  //   selectedIds,
  //   onSelectAll,
  //   onSelectOne,
  onEdit,
  onDelete,
  loading,
  devices,
  onAddDevice,
  onShowCode,
  onNewCode,
  onSignOut,
  onRevokeDevice,
}) => {
  const { t } = useTranslation("vehicles");

  /** Whether this table has been given anything to say about devices. */
  const showsDevices = Boolean(devices);

  //   const isAllSelected =
  //     vehicles.length > 0 && selectedIds.length === vehicles.length;
  //   const isIndeterminate =
  //     selectedIds.length > 0 && selectedIds.length < vehicles.length;

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-12 bg-gray-100 border-b border-gray-200"></div>
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="h-16 bg-gray-50 border-b border-gray-100"
          ></div>
        ))}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto max-h-[50svh] md:max-h-[55vh] lg:max-h-[60vh] xl:max-h-[70vh] overflow-y-scroll border-b border-b-gray-300">
      <table className="min-w-full border-collapse border border-gray-300">
        <thead className="bg-gray-50 text-nowrap sticky top-0 z-1">
          <tr>
            {/* <th className="w-12 px-6 py-3 text-left">
              <input
                type="checkbox"
                checked={isAllSelected}
                ref={(input) => {
                  if (input) input.indeterminate = isIndeterminate;
                }}
                onChange={(e) => onSelectAll(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </th> */}

            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
              {t("table.columns.id")}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
              {t("table.columns.licensePlate")}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
              {t("table.columns.displayName")}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
              {t("table.columns.type")}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
              {t("table.columns.markModel")}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
              {t("table.columns.numberOfAxles")}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
              {t("table.columns.weightCapacity")}
            </th>
            {showsDevices && (
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
                {t("table.columns.device")}
              </th>
            )}
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200">
              {t("table.columns.actions")}
            </th>
          </tr>
        </thead>

        <tbody className="bg-white">
          {vehicles.map((vehicle) => {
            // Joined by vehicle id rather than fetched per row: the devices are
            // loaded once for the page, and a truck has at most one.
            const device = devices?.[vehicle._id];

            return (
            <tr
              key={vehicle._id}
              className="hover:bg-gray-50 transition-colors duration-150"
            >
              {/* <td className="px-6 py-4">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(vehicle._id)}
                  onChange={(e) => onSelectOne(vehicle._id, e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </td> */}

              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-500 border border-gray-200">
                {vehicle._id.slice(-6).toUpperCase()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-500 border border-gray-200">
                {vehicle.registrationNumber}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                {vehicle.displayName || t("table.na")}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                {vehicle.type || t("table.na")}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                {vehicle.markAndModel || t("table.na")}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                {vehicle.numberOfAxles || 0}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                {vehicle.weightCapacity || 0} {t("table.weightUnit")}
              </td>
              {showsDevices && (
                <td className="px-6 py-4 whitespace-nowrap text-sm border border-gray-200">
                  {vehicle.type !== "TRUCK" ? (
                    // A trailer has no cab, so "no device" would read as
                    // something outstanding rather than as nothing to do.
                    <span className="text-gray-300">{t("table.na")}</span>
                  ) : !device ? (
                    <span className="text-gray-400">
                      {t("device.status.none")}
                    </span>
                  ) : device.pairing.pending ? (
                    <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
                      {t("device.status.awaitingPairing")}
                    </span>
                  ) : device.signedInDriver ? (
                    <span className="inline-flex items-center gap-1.5 text-gray-600">
                      <Smartphone className="w-3.5 h-3.5 text-green-600" />
                      {device.signedInDriver.firstName}{" "}
                      {device.signedInDriver.lastName}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-gray-500">
                      <Smartphone className="w-3.5 h-3.5 text-gray-400" />
                      {t("device.status.idle")}
                    </span>
                  )}
                </td>
              )}
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium border border-gray-200">
                <div className="flex items-center justify-end">
                  <ActionMenu
                    ariaLabel={t("table.columns.actions")}
                    items={
                      [
                        {
                          label: t("actions.edit"),
                          icon: <Eye className="w-4 h-4" />,
                          onClick: () => onEdit(vehicle),
                        },

                        /*
                          The device entries, offered only where they mean
                          something: on a truck, and matched to what that truck's
                          screen currently is. A trailer gets none of them, and a
                          truck with no device is offered one act — set it up.
                        */
                        ...(vehicle.type === "TRUCK" && showsDevices
                          ? !device
                            ? onAddDevice
                              ? [
                                  {
                                    label: t("device.actions.add"),
                                    icon: <Smartphone className="w-4 h-4" />,
                                    onClick: () => onAddDevice(vehicle),
                                    divider: true,
                                  },
                                ]
                              : []
                            : ([
                                // Reading the outstanding code comes first, and
                                // deliberately above regenerating: for a screen
                                // already in service, a new code is an outage
                                // until it is redeemed.
                                ...(onShowCode && device.pairing.pending
                                  ? [
                                      {
                                        label: t("device.actions.showCode"),
                                        icon: <KeyRound className="w-4 h-4" />,
                                        onClick: () => onShowCode(device),
                                        divider: true,
                                      },
                                    ]
                                  : []),
                                ...(onSignOut && device.signedInDriver
                                  ? [
                                      {
                                        label: t("device.actions.signOut"),
                                        icon: <UserMinus className="w-4 h-4" />,
                                        onClick: () => onSignOut(device),
                                      },
                                    ]
                                  : []),
                                ...(onNewCode
                                  ? [
                                      {
                                        label: t("device.actions.newCode"),
                                        icon: <RefreshCw className="w-4 h-4" />,
                                        onClick: () => onNewCode(device),
                                      },
                                    ]
                                  : []),
                                ...(onRevokeDevice
                                  ? [
                                      {
                                        label: t("device.actions.revoke"),
                                        icon: <Smartphone className="w-4 h-4" />,
                                        onClick: () => onRevokeDevice(device),
                                        variant: "danger",
                                      },
                                    ]
                                  : []),
                              ] as ActionMenuItem[])
                          : []),

                        {
                          label: t("actions.delete"),
                          icon: <Trash2 className="w-4 h-4" />,
                          onClick: () => onDelete(vehicle._id),
                          variant: "danger",
                          divider: true,
                        },
                      ] as ActionMenuItem[]
                    }
                  />
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>

      {vehicles.length === 0 && !loading && (
        <div className="text-center py-12">
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {t("table.empty.title")}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {t("table.empty.description")}
          </p>
        </div>
      )}
    </div>
  );
};
